import { useTransactionStats } from '../../../hooks/queries/useTransactionStats';
import { PATHS } from '../../../paths';
import { usePathWithDateRange } from '../../../providers/DateRangeProvider';

import MerchantSpendingSectionBase from './MerchantSpendingSectionBase';

export interface IMerchantSpendingSectionProps {
  /** The record's merchant. Without one there is nothing to total, so nothing renders. */
  merchantId: string | null | undefined;
  /** The record's amount as the API serializes it. */
  amount: string;
  /** Whether the record is hidden from the household's totals. An ignored debit still gets a marker but is not one of the counted debits. */
  isIgnored?: boolean;
  /**
   * Whether the record is one of the debits the stats count. Defaults to a
   * positive, non-ignored amount — the transaction case. A record that is not
   * itself a transaction states it: a bill is in the aggregate only once paid,
   * through its payment.
   */
  currentInStats?: boolean;
  className?: string;
}

/**
 * `MerchantSpendingSectionBase` wired to `getTransactionStats` for the record's
 * merchant: all-time, no date window. The "View merchant" link carries the
 * pane's current date range when there is one, as the other merchant links do.
 * Hidden when the record has no merchant, when the stats resolve to null
 * (nothing spent there, or a mixed-currency set), when the record is the
 * merchant's only counted debit, and on a failed read.
 */
const MerchantSpendingSection = ({
  merchantId,
  amount,
  isIgnored = false,
  currentInStats,
  className,
}: IMerchantSpendingSectionProps) => {
  const { stats, loading, error } = useTransactionStats(merchantId ? { kind: 'merchant', merchantId } : null);
  const addDateRangeToPath = usePathWithDateRange();

  // ApolloProvider's errorLink already reports the failure; a secondary section hides rather than breaking the page.
  if (!merchantId || error) {
    return null;
  }

  const currentAmount = currentAmountFor(amount);

  return (
    <MerchantSpendingSectionBase
      stats={stats}
      loading={loading}
      currentAmount={currentAmount}
      currentInStats={currentInStats ?? (currentAmount !== null && !isIgnored)}
      viewMerchantPath={addDateRangeToPath(PATHS.MERCHANT(merchantId))}
      className={className}
    />
  );
};

/**
 * Only a positive amount is a debit, the population the stats describe. A refund
 * or a zero amount has no place on the spending scale, so it gets no marker.
 */
function currentAmountFor(amount: string): number | null {
  const value = Number(amount);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export default MerchantSpendingSection;
